/**
 * Whether this document is under the `@storage` Service Worker's control, and
 * what to do when it is not (见 18).
 *
 * A force reload (Ctrl/Cmd+Shift+R) is specified to bypass the Service Worker,
 * and not merely for the navigation: the resulting document stays uncontrolled
 * for its **whole lifetime** (`navigator.serviceWorker.controller` returns `null`
 * on a force refresh, per the Service Workers spec), so every request it makes
 * escapes to the network. On this runtime that silently removes the two things
 * the network has no files for — `@storage` media and the code root of a
 * delivered or side-loaded module, both of which live only in OPFS — and the
 * origin answers each with an honest `No such platform file` 404.
 *
 * It needs saying precisely because so little else breaks: byte-shaped reads
 * (wasm, nls, a delivered service's blob `import`) never went through the worker,
 * so the app looks healthy while images and linked modules are simply gone.
 *
 * A plain reload restores control, so the treatment is one reload — with the same
 * breaker `generation.ts` carries, for the same reason: a mechanism that reloads
 * to heal itself must be unable to loop.
 *
 * Kept free of DOM types (a narrow storage interface, no `window`) so the rule is
 * unit-testable — same shape and same reason as `connection-lock.ts`.
 */

/** The slice of `sessionStorage` this needs. Per-tab, which is the right scope. */
export interface SwControlStoreLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * - `controlled` / `activating`: nothing to do.
 * - `reload`: this document was bypassed; one reload brings it back.
 * - `recovered`: controlled again, and a recovery attempt was outstanding — the
 *   only chance to leave a trace of it, since the `reload` that fixed it took the
 *   page's log sink with it (见 18 可观测性的局限). Without this, "the page
 *   loaded twice" has no explanation anywhere.
 * - `stuck`: bypassed, and reloading already failed to fix it (or the breaker
 *   cannot be armed), so report it instead of reloading again.
 */
export type SwControlAction =
  | 'controlled'
  | 'activating'
  | 'reload'
  | 'recovered'
  | 'stuck';

/** Where a recovery attempt is recorded. */
const RELOAD_MARKER_KEY = 'hibro:sw-control-reload';

/**
 * How long a recorded attempt suppresses the next one.
 *
 * Only relevant when a reload did *not* restore control: a successful recovery
 * clears the marker, so force-refreshing twice in a row is two recoveries rather
 * than one recovery and one refusal.
 */
export const CONTROL_RELOAD_BREAKER_WINDOW_MS = 60_000;

/**
 * Decide what to do about the Service Worker's grip on this document, recording
 * the attempt.
 *
 * `hasActiveWorker` separates the two ways `controlled` can be false. Without an
 * active worker this is simply the first load of the origin — the registration is
 * still installing and `clients.claim()` will take the page over shortly (见 18
 * 首屏窗口) — and reloading there would put an extra page load in front of every
 * first visit. An active worker that is nonetheless not controlling us is the
 * bypass.
 *
 * **A store that cannot be written resolves to `stuck`**, the opposite lean from
 * `decideGenerationAction`. The trade differs: there, refusing to reload strands
 * the tab on a host from another generation (split brain), so reloading is the
 * safer failure. Here, refusing costs one session's media while reloading with no
 * armed breaker risks an endless refresh loop.
 */
export function decideSwControlAction(input: {
  controlled: boolean;
  hasActiveWorker: boolean;
  store: SwControlStoreLike | undefined;
  now: number;
}): SwControlAction {
  const { controlled, hasActiveWorker, store, now } = input;
  if (controlled) {
    const outstanding = readAttempt(store) !== undefined;
    clearAttempt(store);
    return outstanding ? 'recovered' : 'controlled';
  }
  if (!hasActiveWorker) {
    return 'activating';
  }
  const previous = readAttempt(store);
  if (
    previous !== undefined &&
    now - previous < CONTROL_RELOAD_BREAKER_WINDOW_MS
  ) {
    return 'stuck';
  }
  return recordAttempt(store, now) ? 'reload' : 'stuck';
}

function readAttempt(
  store: SwControlStoreLike | undefined,
): number | undefined {
  try {
    const raw = store?.getItem(RELOAD_MARKER_KEY);
    if (!raw) {
      return undefined;
    }
    const at = Number(raw);
    return Number.isFinite(at) ? at : undefined;
  } catch {
    return undefined;
  }
}

/** Arm the breaker. `false` means it could not be armed — see the lean above. */
function recordAttempt(
  store: SwControlStoreLike | undefined,
  now: number,
): boolean {
  try {
    if (!store) {
      return false;
    }
    store.setItem(RELOAD_MARKER_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

function clearAttempt(store: SwControlStoreLike | undefined): void {
  try {
    store?.removeItem(RELOAD_MARKER_KEY);
  } catch {
    // A marker that cannot be cleared only costs one refused recovery inside the
    // breaker window, and it expires on its own.
  }
}
